/**
 * The system clipboard, as far as a page may use it: plain text out on every copy, and plain text in only when Ctrl+V
 * asks for it (the browser may ask the person once). Neither ever throws: a refusal, a missing API or a page without
 * focus reads as nothing written, or nothing to read.
 */
export interface SystemClipboard {
  /** Whether the text reached the system clipboard. */
  write(text: string): Promise<boolean>;
  /** The system clipboard's text, or `undefined` when there is none or the browser refused. */
  read(): Promise<string | undefined>;
}

type TextClipboard = Pick<Clipboard, "readText" | "writeText">;

export function systemClipboard(clipboard: TextClipboard | undefined): SystemClipboard {
  return {
    async write(text) {
      if (clipboard === undefined) return false;
      try {
        await clipboard.writeText(text);
        return true;
      } catch {
        return false;
      }
    },
    async read() {
      if (clipboard === undefined) return undefined;
      try {
        return await clipboard.readText();
      } catch {
        return undefined;
      }
    },
  };
}

/** The browser's, where the page has one. */
export function browserClipboard(): SystemClipboard {
  const clipboard =
    typeof navigator === "undefined"
      ? undefined
      : (navigator.clipboard as TextClipboard | undefined);
  return systemClipboard(clipboard);
}
